/* eslint-disable @typescript-eslint/no-explicit-any */
'use client';

// 3rd Party Imports
import React, { useEffect, useRef, useState, useCallback } from 'react';
import { usePathname } from 'next/navigation';
import Link from 'next/link';
import Image from 'next/image';
import classNames from 'classnames';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronDown } from '@fortawesome/free-solid-svg-icons';
import { useSession } from 'next-auth/react';

// Component Imports
import Toggle from '@/components/utility/toggle';

// Data Imports
import { navMasthead } from '@/lib/data/site/navMasthead';

// Type Imports
import { NavLink } from '@/lib/types/navLink';

// Custom Code Imports
import { strings } from '@/lib/data/site/strings';

// Context Imports
import { useUserInterface } from '@/lib/context/userInterfaceContext';
import { setMenuStatus } from '@/lib/context/userInterfaceActions';
import { useOrder } from '@/lib/context/orderContext';
import { clearOldCookies } from '@/lib/functions/clearOldCookies';
import { getOrderByToken } from '@/lib/functions/getOrderByToken';
import { isNavLinkActive } from '@/lib/functions/isNavLinkActive';
import { useSubscribeToCartRefresh } from '@/lib/broadcast/cartSync';
import type { CartSyncMessage } from '@/lib/types/boadcast/message';

// Style Imports
import styles from './masthead.module.scss';

// interface Logo {
//   src: string;
//   width: number;
//   height: number;
//   alt: string;
// }

// interface Brand {
//   logo: Logo;
//   logoMobile: Logo;
// }

// interface BrandData {
//   [key: string]: Brand;
// }

export default function MastHead() {
  const pathname = usePathname();

  const { state: uiState, dispatch: uiDispatch } = useUserInterface();
  const isMenuOpen = uiState.menuOpen || false;
  const brand = "partysupplies";
  const [isClient, setIsClient] = useState(false);
  const [openNavDropdown, setOpenNavDropdown] = useState<string | null>(null);
  const cookiesCleanedRef = useRef(false);
  const navRef = useRef<HTMLElement>(null);

  useEffect(() => setIsClient(true), []);

  useEffect(() => {
    if (!cookiesCleanedRef.current) {
      clearOldCookies();
      cookiesCleanedRef.current = true;
    }
  }, []);

  const toggleMenu = () => setMenuStatus(uiDispatch, !isMenuOpen);
  const handleNavDropdownToggle = (label: string) => {
    setOpenNavDropdown((current) => (current === label ? null : label));
  };

  useEffect(() => {
    setOpenNavDropdown(null);
  }, [pathname]);

  useEffect(() => {
    if (!isMenuOpen) {
      setOpenNavDropdown(null);
    }
  }, [isMenuOpen]);

  useEffect(() => {
    if (!openNavDropdown) {
      return;
    }

    const handleClickOutside = (event: globalThis.MouseEvent) => {
      if (navRef.current && !navRef.current.contains(event.target as Node)) {
        setOpenNavDropdown(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [openNavDropdown]);

  const { state: orderState, dispatch: orderDispatch } = useOrder();
  const { data: session } = useSession();

  const handleCartRefresh = useCallback((message: CartSyncMessage) => {
    if (message.type !== 'cart_refreshed') return;
    const token = message.token ?? orderState?.token ?? '';
    void getOrderByToken(orderState, orderDispatch, token);
  }, [orderDispatch, orderState]);

  useSubscribeToCartRefresh(handleCartRefresh, 1000);

  useEffect(() => {
    setIsClient(true);
  }, []);

  return (
    <>
      <header id="masthead" className={styles.masthead} role="banner">
        <a href="#main-content" className={styles.skipLink}>Skip to Content</a>
        <a href="#main-navigation" className={styles.skipLink}>Skip to Navigation</a>

        <div className={styles.mastheadInner}>
          <div className={styles.mastheadTop}>
            <div className={styles.mastheadContentRow}>
              {/* Mobile Menu Toggle */}
              <button
                className={classNames(
                  styles.mastheadMobileToggle,
                  { [styles.open]: isClient && isMenuOpen },
                )}
                {...(isClient ? { "aria-expanded": isMenuOpen, "aria-label": isMenuOpen ? "Close menu" : "Open menu" } : {})}
                onClick={toggleMenu}
              >
                <span>{isClient ? (isMenuOpen ? "Open" : "Close") : ""} Menu</span>
              </button>

              {/* Masthead Logos */}
              <div className={classNames(styles.mastheadLogos, styles[brand])}>
                <Link href="/" className={classNames(styles.logo, styles[brand])}>
                  <Image
                    src="/images/logo-white.svg"
                    alt={strings.brandName}
                    width={200}
                    height={73}
                    priority
                  />
                </Link>
                <Link href="/" className={classNames(styles.logoMobile, styles[brand])}>
                  <Image
                    src="/images/logo-white.svg"
                    alt={strings.brandName}
                    width={170}
                    height={63}
                    priority
                  />
                </Link>
              </div>

              {/* Masthead Main Navigation */}
              <div className={classNames(styles.mainNav, { [styles.mainNavOpen]: isClient && isMenuOpen })}>
                <nav
                  ref={navRef}
                  id="main-navigation"
                  aria-label="Main Navigation"
                  className={classNames(styles.nav, styles[brand])}
                >
                  <ul>
                    {navMasthead.map(({ href, label, mobileOnly, children }: NavLink) => {
                      const dropdownKey = label ?? href ?? '';
                      return (
                        <li key={dropdownKey} className={classNames({ [styles.hasSubmenu]: children, [styles.mobileOnly]: mobileOnly })}>
                          {!children && (
                            <Link
                              href={href ?? '#'}
                              className={classNames({ [styles.active]: isNavLinkActive(pathname, href, !children) })}
                              onClick={toggleMenu}
                            >
                              {label}
                            </Link>
                          )}

                          {children && dropdownKey && (
                            <Toggle
                              isOpen={openNavDropdown === dropdownKey}
                              onToggle={() => handleNavDropdownToggle(dropdownKey)}
                              clickElement={
                                <span
                                  className={classNames(styles.navTrigger, styles.level1, {
                                    [styles.active]: isNavLinkActive(pathname, href),
                                  })}
                                >
                                  {label}
                                  <FontAwesomeIcon icon={faChevronDown} />
                                </span>
                              }
                              clickClass={styles.toggle}
                              clickActiveClass={styles.toggleOpen}
                              childClass={styles.collapsed}
                              noToggleClass=""
                            >
                              <ul className={classNames(styles.subMenu, { [styles.disabled]: isClient })}>
                                {href && (
                                  <li>
                                    <Link
                                      href={href}
                                      onClick={toggleMenu}
                                      className={classNames(styles.viewAllLink, {
                                        [styles.active]: isNavLinkActive(pathname, href, true),
                                      })}
                                    >
                                      Explore All {label}
                                    </Link>
                                    <hr className={styles.subMenuDivider} />
                                  </li>
                                )}
                                {children.map((childGroup: any, index: number) => (
                                  <li key={index}>
                                    {childGroup.map(({ href, label, divider, children }: NavLink) => (
                                      <span key={label}>
                                        <Link href={href ?? '#'} onClick={toggleMenu} className={classNames(styles.level1, { [styles.active]: isNavLinkActive(pathname, href, true) })}>
                                          {label}
                                        </Link>
                                        {children && (
                                          <ul>
                                            {children.map(({ href, label }: NavLink) => (
                                              <li key={label}>
                                                <Link href={href ?? '#'} onClick={toggleMenu} className={styles.level2}>
                                                  {label}
                                                </Link>
                                              </li>
                                            ))}
                                          </ul>
                                        )}
                                        {divider && <hr />}
                                      </span>
                                    ))}
                                  </li>
                                ))}
                              </ul>
                            </Toggle>
                          )}
                        </li>
                      );
                    })}
                  </ul>
                </nav>
              </div>

              {/* Masthead Utility Navigation */}
              <div className={styles.utilityNav}>
                <Link
                  href="/my-account/"
                  className={classNames(styles.accountLink, { [styles.loggedIn]: session })}
                >
                  <span>{session ? 'My Account' : 'Log In'}</span>
                </Link>
                <Link href="/cart/" className={styles.cartLink}>
                  {(isClient && orderState?.itemCount) && (
                    orderState.itemCount
                  )}
                  <span className="screenReaderText">My Cart</span>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </header>
    </>
  );
}
